export const dynamic = 'force-dynamic';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import { useIwEditingStore, useIwExecutionStore } from '@/entities/interwills';
import { getPersonFullName } from '@/shared/helpers/names';
import { dates } from '@/shared/utils/dates';

import { initFonts } from '../LastWillPDF/helpers';

import { DisplayPlaceholder } from './DisplayPlaceholder';

import { styles } from './styles';

initFonts();

export const WillCertificatePDF = () => {
  const { testator, witnesses, executor, subExecutor } = useIwEditingStore.getState();
  const {
    createdDate,
    willNumber,
    storageLocation,
    signingDate,
    signingPlace,
    witnessVerifications,
    testatorVerification,
    witnessDocuments,
  } = useIwExecutionStore.getState();

  const fullName = getPersonFullName(testator);

  return (
    <Document style={styles.document}>
      <Page size={{ width: 794, height: 1056 }} style={styles.page} break={false} wrap={false}>
        <View style={styles.watermark}>
          <Text>LAST WILL AND TESTAMENT</Text>
          <Text>of</Text>
          <Text>{fullName}</Text>
        </View>

        <View style={styles.header}>
          <Text>{createdDate && dates.formatLong(createdDate)}</Text>

          <Text>Last Will and Testament of {fullName}</Text>
          <Text>
            Number <Text>{willNumber}</Text>
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.title}>CERTIFICATE OF LAST WILL AND TESTAMENT</Text>
          <Text>
            This certificate confirms that the Last Will and Testament of {fullName} number{' '}
            {willNumber}{' '}
            {signingDate && signingPlace && (
              <>
                was signed on{' '}
                <DisplayPlaceholder
                  text={signingDate && dates.formatLong(signingDate)}
                  placeholder="Signed Date"
                />{' '}
                at <DisplayPlaceholder text={signingPlace} placeholder="Signed Place" /> in the
                presence of {witnesses.length} witnesses
              </>
            )}
            .
          </Text>
          {storageLocation &&
            testatorVerification?.attachmentNumber &&
            testatorVerification.createdAt &&
            testatorVerification.duration && (
              <>
                <Text>
                  Storage location of the original signed Will:{' '}
                  <DisplayPlaceholder text={storageLocation} placeholder="Storage location" />.
                </Text>
                <Text>
                  The Testator made a video verification confirming that the Will was executed
                  voluntarily and without any undue influence or coercion.
                </Text>
                {testatorVerification && (
                  <Text>
                    Video number: {testatorVerification.attachmentNumber}, date and time:{' '}
                    {dates.formatDateTimeInterwill(new Date(testatorVerification.createdAt))},
                    duration: {dates.formatSecondsToMmSs(testatorVerification.duration)}.
                  </Text>
                )}
                <Text>Storage location of the original video: Owner.One Repository.</Text>
              </>
            )}
          <Text style={styles.subtitle}>The Witnesses to the execution of the Will were:</Text>

          <View style={styles.witnesses}>
            {witnesses.map((item, i) => {
              if (!item.id) return null;
              return (
                <View style={styles.witness} key={item.id}>
                  <View style={styles.witnessTitle}>
                    <Text>
                      Witness {i + 1}: {getPersonFullName(item)}
                    </Text>
                  </View>
                  {witnessDocuments[item.id] && (
                    <View style={{ gap: 4 }}>
                      <Text>Signed verification of Witness {i + 1}</Text>
                      <Text>Number: {witnessDocuments[item.id].attachmentNumber}</Text>
                      <Text>
                        Date of signature:{' '}
                        {dates.formatLongDate(witnessDocuments[item.id].createdAt)}
                      </Text>
                    </View>
                  )}
                  {witnessVerifications[item.id] && (
                    <View style={{ gap: 4 }}>
                      <Text>Video verification of Witness {i + 1}</Text>
                      <Text>Number: {witnessVerifications[item.id].attachmentNumber}</Text>
                      <Text>
                        Date and time:{' '}
                        {dates.formatDateTimeInterwill(
                          new Date(witnessVerifications[item.id].createdAt),
                        )}
                      </Text>
                      <Text>
                        Duration:{' '}
                        {dates.formatSecondsToMmSs(witnessVerifications[item.id].duration)}
                      </Text>
                    </View>
                  )}
                </View>
              );
            })}
          </View>

          {(executor || subExecutor) && (
            <View style={styles.executors}>
              <Text style={styles.subtitle}>The Executors named in the Will are:</Text>
              {executor && <Text>Executor: {getPersonFullName(executor)}</Text>}
              {subExecutor && <Text>Substitute Executor 1: {getPersonFullName(subExecutor)}</Text>}
            </View>
          )}
        </View>

        <View style={styles.footer} fixed>
          <View fixed>
            <Text>Powered by the International Will by the Owner.One Repository</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
